import React, { useState, useEffect } from "react"; 
import { editClient } from '../../controllers/api_clients';
import { changePassword } from "../../controllers/apiProfile"; 
import { getTypeTaxpayer } from '../../controllers/api_type_taxpayer';
import api from "../../services/axiosConfig";
import { toast, ToastContainer } from "react-toastify";
import LogoUploader from "../../components/LogoUploader/LogoUploader";
import "react-toastify/dist/ReactToastify.css";

export default function ProfileContent({ client: initialClient, profile: initialProfile }) {

  const isClient = !!initialClient;
  const [data, setData] = useState(initialClient || initialProfile);

  const [typeTaxpayer, setTypeTaxpayer] = useState([]);
  const [actividades, setActividades] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  // Estados Modal Cambio de Contraseña
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const queryParams = new URLSearchParams(location.search);
  const forceChangePassword = queryParams.get("forceChangePassword") === "true";

  // Cargar tipos de contribuyente solo si es cliente
  useEffect(() => {
    if (!isClient) return;

    const fetchTypeTaxpayer = async () => {
      try {
        const data = await getTypeTaxpayer();
        setTypeTaxpayer(data);
      } catch (err) {
        console.error("Error al cargar tipos de contribuyente", err);
      }
    };
    fetchTypeTaxpayer();
  }, [isClient]);

  // Cargar catálogo de actividades económicas (relación FK) solo si es
  // cliente. Se carga una sola vez con el MISMO cliente axios autenticado
  // (token vía interceptor) que usan los servicios del proyecto.
  useEffect(() => {
    if (!isClient) return;

    const fetchActividades = async () => {
      try {
        const response = await api.get("/api/config/actividades-economicas");
        setActividades(response.data);
      } catch (err) {
        console.error("Error al cargar actividades económicas", err);
      }
    };
    fetchActividades();
  }, [isClient]);

  // (2026-05-15) El cambio de contraseña obligatorio (primer login,
  // must_change_password) aplica a TODOS los roles, incluidos auditor/admin
  // (que NO son "cliente"). Antes esta lógica estaba dentro del effect con
  // `if (!isClient) return;`, por lo que a un auditor nunca se le abría el
  // modal. Se separa en su propio effect sin esa restricción.
  useEffect(() => {
    if (forceChangePassword) {
      setShowPasswordModal(true);
      const url = new URL(window.location);
      url.searchParams.delete("forceChangePassword");
      window.history.replaceState({}, "", url);
    }
  }, [forceChangePassword]);

  // Guardar cambios del perfil cliente
  const handleSave = async () => {
    if (!isClient) return;

    setLoading(true);
    try {
      const updatedClient = await editClient(data.id, data);
      setData(updatedClient);
      toast.success("Perfil actualizado correctamente");
      setIsEditing(false);
    } catch (err) {
      toast.error("Error al actualizar el perfil");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Cambiar contraseña
  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      return toast.error("Las contraseñas no coinciden");
    }

    try {
      setLoading(true);

      await changePassword({
        old_password: oldPassword,
        new_password: newPassword
      });

      toast.success("Contraseña cambiada con éxito");
      // (2026-07-05) La nueva vigencia (180 días) arranca ahora: ocultar el
      // banner de vencimiento para el resto de la sesión (el estado fresco se
      // recargará en el próximo login).
      sessionStorage.setItem("pwd_banner_dismissed", "1");
      setShowPasswordModal(false);
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.error || "Error al cambiar contraseña");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center px-4 pb-10 animate-sirius-fade-up">
      <ToastContainer />

      <div className="w-full max-w-4xl">
        {/* --- TARJETA PRINCIPAL --- */}
        <div className="relative bg-white rounded-2xl border border-slate-200/70 shadow-sirius overflow-hidden">

          {/* Banda superior con gradiente Sirius */}
          <div className="h-36 bg-gradient-to-br from-sirius-600 via-spark-500 to-sirius-800 relative">
            <div className="absolute inset-0 opacity-30 bg-sirius-mesh" />
          </div>

          {/* Avatar / Logo */}
          <div className="absolute top-[88px] left-1/2 -translate-x-1/2">
            {isEditing && isClient ? (
              <LogoUploader
                logo={data.logo}
                size={32}//24, 32, 48
                mode="edit"
                onChange={(base64) =>
                  setData({ ...data, logo: base64 })
                }
              />
            ) : (
              <div className="w-28 h-28 rounded-2xl border-4 border-white shadow-sirius-lg overflow-hidden bg-sirius-50">
                {data.logo ? (
                  <img
                    src={data.logo}
                    alt="Logo"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center w-full h-full text-4xl font-display font-bold text-sirius-600">
                    {data.nombre_empresa?.[0] || data.nombre?.[0] || "?"}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="px-6 md:px-10 pt-20 pb-8">
            {/* NOMBRE + EMAIL */}
            <div className="text-center mb-8">
              {isEditing && isClient ? (
                <div className="flex flex-col items-center gap-2">
                  <input
                    type="text"
                    value={data.nombre_empresa}
                    onChange={(e) => setData({ ...data, nombre_empresa: e.target.value.toUpperCase() })}
                    className="sirius-input max-w-sm text-center text-xl font-display font-bold"
                  />
                  <input
                    type="email"
                    value={data.email}
                    onChange={(e) => setData({ ...data, email: e.target.value.toUpperCase() })}
                    className="sirius-input max-w-sm text-center text-ink-500"
                  />
                </div>
              ) : (
                <>
                  <h2 className="text-2xl md:text-3xl font-display font-bold text-ink-900">
                    {data.nombre_empresa || data.nombre}
                  </h2>
                  <p className="text-ink-500 mt-1">{data.email}</p>

                  {!isClient && (
                    <span className="sirius-badge mt-3">
                      Rol: {data.rol}
                    </span>
                  )}
                </>
              )}
            </div>

            {/* --- CAMPOS SOLO CLIENTES --- */}
            {isClient && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">

                {["rif", "denominacion_comercial", "telefono", "direccion", "zona", "estado", "region"].map((field) => (
                  <div key={field} className="bg-slate-50/70 rounded-xl border border-slate-200/70 p-4">
                    <h3 className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-500 mb-1.5">{field.replace('_', ' ').toUpperCase()}</h3>
                    {isEditing ? (
                      <input
                        type="text"
                        value={data[field] || ""}
                        onChange={(e) => setData({ ...data, [field]: e.target.value.toUpperCase() })}
                        className="sirius-input"
                      />
                    ) : (
                      <p className="text-sm font-medium text-ink-900 break-words">{data[field] || "—"}</p>
                    )}
                  </div>
                ))}

                {/* --- DATOS FISCALES / IDENTIFICACIÓN (opcionales) --- */}
                {/* Espejan el patrón de los campos de texto de arriba: mismo
                    contenedor/clases, mismo binding a `data`, .toUpperCase() en
                    onChange y `sirius-input`. Viajan en el payload de editClient
                    (PUT /empresas) porque se envía el objeto `data` completo. */}
                <div className="bg-slate-50/70 rounded-xl border border-slate-200/70 p-4">
                  <h3 className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-500 mb-1.5">Serie Fiscal</h3>
                  {isEditing ? (
                    <input
                      type="text"
                      value={data.serie || ""}
                      onChange={(e) => setData({ ...data, serie: e.target.value.toUpperCase() })}
                      className="sirius-input"
                    />
                  ) : (
                    <p className="text-sm font-medium text-ink-900 break-words">{data.serie || "—"}</p>
                  )}
                </div>

                <div className="bg-slate-50/70 rounded-xl border border-slate-200/70 p-4">
                  <h3 className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-500 mb-1.5">Código de Operación</h3>
                  {isEditing ? (
                    <input
                      type="text"
                      value={data.cod_operacion || ""}
                      onChange={(e) => setData({ ...data, cod_operacion: e.target.value.toUpperCase() })}
                      className="sirius-input"
                    />
                  ) : (
                    <p className="text-sm font-medium text-ink-900 break-words">{data.cod_operacion || "—"}</p>
                  )}
                </div>

                <div className="bg-slate-50/70 rounded-xl border border-slate-200/70 p-4">
                  <h3 className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-500 mb-1.5">Actividad Económica</h3>
                  {isEditing ? (
                    <select
                      value={data.actividad_economica_id || ""}
                      onChange={(e) => setData({ ...data, actividad_economica_id: e.target.value ? Number(e.target.value) : null })}
                      className="sirius-input"
                    >
                      <option value="">— Seleccione —</option>
                      {actividades.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.codigo + " – " + a.descripcion}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-sm font-medium text-ink-900 break-words">
                      {(() => {
                        const sel = actividades.find((a) => a.id === Number(data.actividad_economica_id));
                        return sel ? sel.codigo + " – " + sel.descripcion : "—";
                      })()}
                    </p>
                  )}
                </div>

                {/* Tipo contribuyente */}
                <div className="bg-slate-50/70 rounded-xl border border-slate-200/70 p-4">
                  <h3 className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-500 mb-1.5">Tipo de Contribuyente</h3>
                  {isEditing ? (
                    <select
                      value={data.tipo_contribuyente_id || ""}
                      onChange={(e) =>
                        setData({ ...data, tipo_contribuyente_id: parseInt(e.target.value) })
                      }
                      className="sirius-input"
                    >
                      <option value="">Seleccione...</option>
                      {typeTaxpayer.map((tt) => (
                        <option key={tt.id} value={tt.id}>
                          {tt.nombre}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-sm font-medium text-ink-900">
                      {data.tipo_contribuyente?.nombre || "—"}
                    </p>
                  )}
                </div>

                <div className="bg-slate-50/70 rounded-xl border border-slate-200/70 p-4">
                  <h3 className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-500 mb-1.5">Fecha de Registro</h3>
                  <p className="text-sm font-medium text-ink-900">
                    {new Date(data.created_at).toLocaleDateString("es-VE")}
                  </p>
                </div>
              </div>
            )}

            {/* --- BOTONES --- */}
            <div className="mt-8 flex flex-wrap justify-center gap-3">

              {/* Edición solo si es cliente */}
              {isClient ? (
                isEditing ? (
                  <>
                    <button
                      className="sirius-btn bg-slate-200 text-ink-700 hover:bg-slate-300"
                      onClick={() => setIsEditing(false)}
                      disabled={loading}
                    >
                      Cancelar
                    </button>
                    <button
                      className="sirius-btn-primary"
                      onClick={handleSave}
                      disabled={loading}
                    >
                      {loading ? "Guardando..." : "Guardar"}
                    </button>
                  </>
                ) : (
                  <button
                    className="sirius-btn-primary"
                    onClick={() => setIsEditing(true)}
                  >
                    Editar Perfil
                  </button>
                )
              ) : (
                <p className="text-ink-400 text-sm italic">
                  Este perfil no es editable.
                </p>
              )}

              {/* Cambiar contraseña */}
              {(
                <button id="btnChangePassword"
                  className="sirius-btn bg-rose-500 text-white hover:bg-rose-600"
                  onClick={() => setShowPasswordModal(true)}
                >
                  Cambiar Contraseña
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* --- MODAL CAMBIO DE CONTRASEÑA --- */}
      {showPasswordModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white rounded-2xl shadow-sirius-lg w-full max-w-sm p-6">
            <h3 className="text-lg font-display font-bold mb-5 text-center text-ink-900">Cambiar Contraseña</h3>

            <label className="block text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-500 mb-1.5">Contraseña Actual</label>
            <input
              type="password"
              className="sirius-input mb-4"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
            />

            <label className="block text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-500 mb-1.5">Nueva Contraseña</label>
            <input
              type="password"
              className="sirius-input mb-4"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />

            <label className="block text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-500 mb-1.5">Confirmar Nueva Contraseña</label>
            <input
              type="password"
              className="sirius-input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />

            <div className="flex justify-end mt-6 gap-2">
              <button
                className="sirius-btn bg-slate-200 text-ink-700 hover:bg-slate-300"
                onClick={() => setShowPasswordModal(false)}
              >
                Cancelar
              </button>
              <button
                className="sirius-btn-primary"
                onClick={handleChangePassword}
                disabled={loading}
              >
                {loading ? "Guardando..." : "Cambiar"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
